import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StudentProfile } from '@court-sync/shared';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService } from '@/services/coach';
import { academyStudentService } from '@/services/academyStudentService';
import { Card, CardContent } from '@/components/ui';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { PageHeader } from '@/components/layout/PageHeader';
import { AcademyStudentProfilesTable } from '@/components/students';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { GraduationCap } from 'lucide-react';

const PAGE_SIZE = 20;

export default function CoachStudentsPage() {
  const { academyId, academySlug } = useCoachAcademyStore();
  const navigate = useNavigate();
  const [rosterStudentIds, setRosterStudentIds] = useState<string[]>([]);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStudentPage = useCallback(
    async (page: number, ids: string[]) => {
      if (!academyId || ids.length === 0) return;
      setLoading(true);
      try {
        const res = await academyStudentService.getAcademyStudentsPaginated(academyId, page, PAGE_SIZE, {
          studentIds: ids,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        setStudents(res.data ?? []);
        setPagination(res.pagination ?? null);
      } finally {
        setLoading(false);
      }
    },
    [academyId]
  );

  useEffect(() => {
    if (!academyId) {
      return () => {};
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setRosterStudentIds([]);
      setStudents([]);
      setPagination(null);
      try {
        const schedule = await coachService.getCoachSchedule(academyId);
        if (cancelled) return;

        const ids = [
          ...new Set(
            schedule.bookings
              .filter((b) => b.status === 'confirmed' && b.studentId)
              .map((b) => b.studentId!)
          ),
        ];
        setRosterStudentIds(ids);

        if (ids.length === 0) {
          setStudents([]);
          setPagination(null);
          return;
        }

        const res = await academyStudentService.getAcademyStudentsPaginated(academyId, 1, PAGE_SIZE, {
          studentIds: ids,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        if (cancelled) return;
        setStudents(res.data ?? []);
        setPagination(res.pagination ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [academyId]);

  const handlePageChange = useCallback(
    (page: number) => {
      void loadStudentPage(page, rosterStudentIds);
    },
    [loadStudentPage, rosterStudentIds]
  );

  if (!academyId) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-gray-500 dark:text-gray-400">Select an academy to view students.</p>
      </div>
    );
  }

  return (
    <div className="contents">
      <PageHeader
        title="Students"
        help={<p>Students enrolled in your lessons</p>}
      />

      {loading && rosterStudentIds.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <p className="text-gray-500 dark:text-gray-400">Loading students...</p>
        </div>
      ) : null}

      {!loading && rosterStudentIds.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No students enrolled in your lessons yet.</p>
          </CardContent>
        </Card>
      ) : null}

      {rosterStudentIds.length > 0 ? (
        <DashboardDataCard>
          <AcademyStudentProfilesTable
          variant="coach"
          students={students}
          pagination={pagination}
          loading={loading}
          emptyMessage="No students found."
          onPageChange={handlePageChange}
          onRowNavigate={(s) => navigate(`/academy/${academySlug}/coach/students/${s.id}`)}
          />
        </DashboardDataCard>
      ) : null}
    </div>
  );
}
